import { create } from 'zustand'
import { notificationsAPI } from '../features/notifications/api'
import {
  isActionableNotification,
  type NotificationBadge,
  type NotificationItem,
  type NotificationsWebSocketFrame,
  type NotificationTab,
} from '../features/notifications/types'
import { useAuthStore } from './authStore'
import { type BackgroundJob, useBackgroundJobsStore } from './backgroundJobsStore'
import { useToastStore } from './toastStore'

const EMPTY_BADGE: NotificationBadge = { unseen: 0, unread: 0, actionable: 0 }
const WS_FALLBACK_DELAY_MS = 10_000
const BADGE_POLL_INTERVAL_MS = 60_000
const MAX_RECONNECT_ATTEMPTS = 5
const SESSION_STARTED_AT = Date.now()

let fallbackDelayTimer: ReturnType<typeof setTimeout> | null = null
let pollingTimer: ReturnType<typeof setInterval> | null = null
let reconnectEnabled = false

const notificationWebSocketUrl = (): string => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${window.location.host}/ws/notifications`
}

const sameNotification = (left: NotificationItem, right: NotificationItem): boolean =>
  String(left.id) === String(right.id)

const matchesActiveFilter = (
  item: NotificationItem,
  tab: NotificationTab,
  category: string,
): boolean => {
  if (category && item.category !== category) return false
  if (tab === 'archived') return Boolean(item.archived_at)
  if (item.archived_at) return false
  return tab !== 'actionable' || isActionableNotification(item)
}

const actorName = (item: NotificationItem): string | undefined =>
  item.actor_name
  || item.actor?.user_name
  || item.actor?.name
  || item.event?.actor_name
  || item.event?.actor?.user_name
  || item.event?.actor?.name
  || undefined

export function shouldToastNotification(item: NotificationItem, pathname: string): boolean {
  if (item.severity === 'warning' || item.severity === 'critical') return true
  if (!item.action_url) return false
  try {
    const actionPath = new URL(item.action_url, window.location.origin).pathname.replace(/\/$/, '')
    const currentPath = pathname.replace(/\/$/, '')
    if (actionPath === currentPath) return true
    const actionParts = actionPath.split('/').filter(Boolean)
    const currentParts = currentPath.split('/').filter(Boolean)
    return actionParts.length > 1
      && currentParts.length > 1
      && actionParts.at(-1) === currentParts.at(-1)
  } catch {
    return false
  }
}

export function isSessionJobNotification(
  item: NotificationItem,
  jobs: Iterable<BackgroundJob>,
  sessionStartedAt = SESSION_STARTED_AT,
): boolean {
  if (item.event_key !== 'job.completed' && item.event_key !== 'job.failed') return false
  const notificationJobId = item.job_id ?? item.entity_id
  for (const job of jobs) {
    const startedAt = new Date(job.startedAt).getTime()
    if (!Number.isFinite(startedAt) || startedAt < sessionStartedAt) continue
    if (item.event_key === 'job.completed' && job.status !== 'completed') continue
    if (item.event_key === 'job.failed' && job.status !== 'failed') continue
    if (notificationJobId !== undefined && String(notificationJobId) === String(job.jobId)) return true
    if (notificationJobId === undefined && job.name && item.title.includes(job.name)) return true
  }
  return false
}

interface NotificationsState {
  badge: NotificationBadge
  items: NotificationItem[]
  tab: NotificationTab
  category: string
  total: number
  page: number
  limit: number
  hasMore: boolean
  loading: boolean
  error: string
  isConnected: boolean
  ws: WebSocket | null
  reconnectAttempts: number

  setTab: (tab: NotificationTab) => void
  setCategory: (category: string) => void
  fetchBadge: () => Promise<void>
  loadNotifications: (reset?: boolean) => Promise<void>
  markSeen: () => Promise<void>
  markAsRead: (id: string | number) => Promise<void>
  markAllAsRead: () => Promise<void>
  archive: (id: string | number) => Promise<void>
  receiveNotification: (
    item: NotificationItem,
    toastAllowed?: boolean,
    contextJobId?: string | number,
  ) => void
  connect: (token: string) => void
  disconnect: () => void
  reset: () => void
}

function clearFallbackPolling(): void {
  if (fallbackDelayTimer) clearTimeout(fallbackDelayTimer)
  if (pollingTimer) clearInterval(pollingTimer)
  fallbackDelayTimer = null
  pollingTimer = null
}

function startFallbackPolling(fetchBadge: () => Promise<void>): void {
  clearFallbackPolling()
  fallbackDelayTimer = setTimeout(() => {
    void fetchBadge()
    pollingTimer = setInterval(() => void fetchBadge(), BADGE_POLL_INTERVAL_MS)
  }, WS_FALLBACK_DELAY_MS)
}

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  badge: EMPTY_BADGE,
  items: [],
  tab: 'actionable',
  category: '',
  total: 0,
  page: 0,
  limit: 20,
  hasMore: true,
  loading: false,
  error: '',
  isConnected: false,
  ws: null,
  reconnectAttempts: 0,

  setTab: (tab) => set({ tab, page: 0, hasMore: true, items: [] }),
  setCategory: (category) => set({ category, page: 0, hasMore: true, items: [] }),

  fetchBadge: async () => {
    try {
      const badge = await notificationsAPI.getBadge()
      set({
        badge: {
          unseen: Number(badge?.unseen ?? 0),
          unread: Number(badge?.unread ?? badge?.unseen ?? 0),
          actionable: Number(badge?.actionable ?? 0),
        },
      })
    } catch {
      // WS/polling badge refresh is best-effort; the inbox remains usable.
    }
  },

  loadNotifications: async (reset = true) => {
    const state = get()
    if (state.loading || (!reset && !state.hasMore)) return
    const nextPage = reset ? 1 : state.page + 1
    set({ loading: true, error: '' })
    try {
      const result = await notificationsAPI.list({
        tab: state.tab,
        category: state.category || undefined,
        page: nextPage,
        limit: state.limit,
      })
      const nextItems = reset
        ? result.notifications
        : [...state.items, ...result.notifications.filter(
          (item) => !state.items.some((existing) => sameNotification(existing, item)),
        )]
      set({
        items: nextItems,
        total: Number(result.total ?? nextItems.length),
        page: Number(result.page ?? nextPage),
        limit: Number(result.limit ?? state.limit),
        hasMore: Boolean(result.hasMore),
        loading: false,
      })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Не удалось загрузить уведомления',
      })
    }
  },

  markSeen: async () => {
    const previousBadge = get().badge
    const timestamp = new Date().toISOString()
    set((state) => ({
      badge: { ...state.badge, unseen: 0 },
      items: state.items.map((item) => item.seen_at ? item : { ...item, seen_at: timestamp }),
    }))
    try {
      await notificationsAPI.markSeen()
    } catch {
      set({ badge: previousBadge })
      void get().fetchBadge()
    }
  },

  markAsRead: async (id) => {
    const timestamp = new Date().toISOString()
    const previous = get()
    const target = previous.items.find((item) => String(item.id) === String(id))
    set((state) => ({
      items: state.items.map((item) => String(item.id) === String(id)
        ? {
          ...item,
          seen_at: item.seen_at || timestamp,
          read_at: item.read_at || timestamp,
        }
        : item),
      badge: target && !target.read_at
        ? {
          ...state.badge,
          unseen: Math.max(0, state.badge.unseen - (target.seen_at ? 0 : 1)),
          unread: Math.max(0, state.badge.unread - 1),
        }
        : state.badge,
    }))
    try {
      await notificationsAPI.markAsRead(id)
    } catch (error) {
      set({ items: previous.items, badge: previous.badge })
      throw error
    }
  },

  markAllAsRead: async () => {
    const timestamp = new Date().toISOString()
    const previous = get()
    set((state) => ({
      items: state.items.map((item) => item.read_at ? item : {
        ...item,
        seen_at: item.seen_at || timestamp,
        read_at: timestamp,
      }),
      badge: { ...state.badge, unseen: 0, unread: 0 },
    }))
    try {
      await notificationsAPI.markAllAsRead()
    } catch (error) {
      set({ items: previous.items, badge: previous.badge })
      throw error
    }
  },

  archive: async (id) => {
    const state = get()
    const item = state.items.find((candidate) => String(candidate.id) === String(id))
    if (!item) return
    const timestamp = new Date().toISOString()
    const nextBadge = {
      unseen: Math.max(0, state.badge.unseen - (item.seen_at ? 0 : 1)),
      unread: Math.max(0, state.badge.unread - (item.read_at ? 0 : 1)),
      actionable: Math.max(0, state.badge.actionable - (isActionableNotification(item) ? 1 : 0)),
    }
    set({
      badge: nextBadge,
      items: state.tab === 'archived'
        ? state.items.map((candidate) => sameNotification(candidate, item)
          ? { ...candidate, archived_at: candidate.archived_at || timestamp }
          : candidate)
        : state.items.filter((candidate) => !sameNotification(candidate, item)),
      total: Math.max(0, state.total - (state.tab === 'archived' ? 0 : 1)),
    })
    try {
      await notificationsAPI.archive(id)
    } catch (error) {
      set({ items: state.items, total: state.total, badge: state.badge })
      throw error
    }
  },

  receiveNotification: (item, toastAllowed = true, contextJobId) => {
    set((state) => {
      const known = state.items.some((existing) => sameNotification(existing, item))
      const matches = matchesActiveFilter(item, state.tab, state.category)
      const items = matches
        ? [item, ...state.items.filter((existing) => !sameNotification(existing, item))]
        : state.items
      return {
        items,
        total: state.total + (matches && !known ? 1 : 0),
        badge: {
          unseen: state.badge.unseen + (known ? 0 : 1),
          unread: state.badge.unread + (known ? 0 : 1),
          actionable: state.badge.actionable + (!known && isActionableNotification(item) ? 1 : 0),
        },
      }
    })

    const backgroundJobs = useBackgroundJobsStore.getState().jobs
    const isSessionJob = contextJobId !== undefined
      ? backgroundJobs.has(String(contextJobId))
      : isSessionJobNotification(item, backgroundJobs.values())
    if (toastAllowed && (shouldToastNotification(item, window.location.pathname) || isSessionJob)) {
      const byline = actorName(item)
      useToastStore.getState().showToast({
        type: item.severity === 'critical' ? 'error' : item.severity,
        title: item.title,
        message: [item.body, byline].filter(Boolean).join(' · ') || item.title,
        duration: item.severity === 'critical' ? 15_000 : 10_000,
        onClick: item.action_url ? () => window.location.assign(item.action_url!) : undefined,
      })
    }
  },

  connect: (token) => {
    reconnectEnabled = true
    const existing = get().ws
    if (existing?.readyState === WebSocket.OPEN) return
    if (existing) {
      existing.onclose = null
      existing.close()
    }

    startFallbackPolling(get().fetchBadge)
    try {
      const ws = new WebSocket(`${notificationWebSocketUrl()}?token=${encodeURIComponent(token)}`)
      set({ ws })

      ws.onopen = () => {
        clearFallbackPolling()
        set({ isConnected: true, reconnectAttempts: 0, ws })
        void get().fetchBadge()
      }

      ws.onmessage = (event) => {
        try {
          const frame = JSON.parse(String(event.data)) as NotificationsWebSocketFrame
          if (frame.type === 'notification.created' && frame.data) {
            const item = frame.data as NotificationItem
            const context = frame.context ?? item.context
            const contextJobId = context?.payload?.jobId
              ?? (item.event_key.startsWith('job.') ? context?.entity?.id : undefined)
            get().receiveNotification(item, frame.toast !== false, contextJobId)
          } else if (frame.type === 'notification.badge' && frame.data) {
            const badge = frame.data as NotificationBadge
            set({
              badge: {
                unseen: Number(badge.unseen || 0),
                unread: Number(badge.unread ?? badge.unseen ?? 0),
                actionable: Number(badge.actionable || 0),
              },
            })
          }
        } catch {
          // Ignore malformed frames without dropping the connection.
        }
      }

      ws.onclose = (event) => {
        set({ isConnected: false, ws: null })
        if (!reconnectEnabled) return
        if (event.code === 4401) {
          reconnectEnabled = false
          useAuthStore.getState().logout()
          window.location.assign('/login?reason=account_deactivated')
          return
        }
        startFallbackPolling(get().fetchBadge)
        const attempt = get().reconnectAttempts
        if (attempt >= MAX_RECONNECT_ATTEMPTS) return
        const delay = Math.min(1000 * (2 ** attempt), 30_000)
        setTimeout(() => {
          if (!reconnectEnabled) return
          const currentToken = useAuthStore.getState().token
          if (!currentToken) return
          set({ reconnectAttempts: attempt + 1 })
          get().connect(currentToken)
        }, delay)
      }
    } catch {
      set({ isConnected: false, ws: null })
    }
  },

  disconnect: () => {
    reconnectEnabled = false
    clearFallbackPolling()
    const ws = get().ws
    if (ws) {
      ws.onclose = null
      ws.close()
    }
    set({ isConnected: false, ws: null, reconnectAttempts: 0 })
  },

  reset: () => {
    get().disconnect()
    set({
      badge: EMPTY_BADGE,
      items: [],
      tab: 'actionable',
      category: '',
      total: 0,
      page: 0,
      hasMore: true,
      loading: false,
      error: '',
    })
  },
}))
